import React, { useMemo, useState } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js';
import { formatPriceWithCents } from 'lib/pricing/ui';
import { ColumnButton } from 'lib/components/ColumnButton';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import {
  ElasticOrderSearchResults,
  ElasticNoticeSearchResults
} from 'lib/types/searchable';
import { Product, PRODUCT_TO_NAME } from 'lib/enums/Product';
import { capitalize } from 'lodash';
import { useReportsLoading } from '../context/LoadingContext';
import { useReportParams } from '../context/ReportParamsContext';
import {
  fetchProductCategoryAggregations,
  fetchNoticesWithParams,
  fetchOrdersWithParams
} from '../utils/elastic';
import { exportSearchableRecordsToCSV } from '../utils/exportOrdersToCSV';
import {
  NEWSPAPER_ORDER_CSV_COLUMNS,
  REPORT_V2_NOTICE_CSV_COLUMNS
} from '../constants/csvColumns';

Chart.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const FILINGTYPE_COLORS = [
  '#2D9BDB', // sky blue - primary accent
  '#00925E', // green - positive indicators
  '#6388C1', // muted blue - secondary data
  '#42B097', // teal - neutral state
  '#F8CA6A', // muted orange - careful use
  '#FFC400', // bright yellow - careful use
  '#24415B', // navy - high contrast
  '#C9D8E6' // light blue-gray - subtle elements
];

interface ProcessedData {
  orderType?: string;
  filingType?: string;
  noticeTypeLabel?: string;
  revenue: number;
  volume: number;
}

enum Metric {
  Revenue = 'revenue',
  Volume = 'volume'
}

export default function ProductCategoryCharts() {
  const { withLoading } = useReportsLoading();
  const { getOrgIds, getDateRange } = useReportParams();
  const { dateRange, startDate, endDate } = getDateRange();

  const ORDER_TYPE_TABS = [
    { label: PRODUCT_TO_NAME[Product.Notice].plural, value: Product.Notice },
    {
      label: PRODUCT_TO_NAME[Product.Obituary].plural,
      value: Product.Obituary
    },
    {
      label: PRODUCT_TO_NAME[Product.Classified].plural,
      value: Product.Classified
    }
  ];
  const METRIC_TABS = [
    { label: Metric.Revenue, value: Metric.Revenue },
    { label: Metric.Volume, value: Metric.Volume }
  ];
  const [selectedOrderType, setSelectedOrderType] = useState(Product.Obituary);
  const [selectedMetric, setSelectedMetric] = useState(Metric.Revenue);

  const {
    value: productCategoryAggsRes,
    isLoading,
    error
  } = useAsyncEffect<{
    orders: ElasticOrderSearchResults;
    notices: ElasticNoticeSearchResults;
  } | null>({
    fetchData: () =>
      withLoading(fetchProductCategoryAggregations(getOrgIds, getDateRange)),
    initialData: null,
    dependencies: [getOrgIds().join(','), dateRange, startDate, endDate],
    errorConfig: {
      service: ColumnService.ELASTIC,
      message:
        '[COLUMN REPORTS V2] Failed to fetch product category aggregations for Products tab',
      tags: {
        orgsFilter: getOrgIds().join(', '),
        dateRange,
        startDateFilter: startDate || '',
        endDateFilter: endDate || ''
      }
    }
  });

  const processedData = useMemo(() => {
    const data: ProcessedData[] = [];

    if (!productCategoryAggsRes) {
      return data;
    }

    const { orders, notices } = productCategoryAggsRes;
    const ordersAggs = orders?.aggregations;
    const noticesAggs = notices?.aggregations;

    ordersAggs?.orderType?.buckets?.forEach(orderTypeBucket => {
      const filingTypeAgg = orderTypeBucket.filingType;
      if (
        !filingTypeAgg ||
        typeof filingTypeAgg !== 'object' ||
        !('buckets' in filingTypeAgg)
      ) {
        return;
      }

      filingTypeAgg.buckets?.forEach((filingTypeBucket: any) => {
        const revenueValue = filingTypeBucket.revenue?.value ?? 0;
        data.push({
          orderType: String(orderTypeBucket.key),
          filingType: String(filingTypeBucket.key),
          revenue: revenueValue,
          volume: filingTypeBucket.doc_count ?? 0
        });
      });
    });

    noticesAggs?.noticeTypeLabel?.buckets?.forEach((noticeTypeBucket: any) => {
      const revenueValue = noticeTypeBucket.revenue?.value ?? 0;
      data.push({
        noticeTypeLabel: String(noticeTypeBucket.key),
        revenue: revenueValue,
        volume: noticeTypeBucket.doc_count ?? 0
      });
    });

    return data;
  }, [productCategoryAggsRes]);

  const filteredData = useMemo(() => {
    if (selectedOrderType === Product.Notice) {
      return processedData.filter(d => d.noticeTypeLabel);
    }
    return processedData.filter(d => d.orderType === selectedOrderType);
  }, [processedData, selectedOrderType]);

  const chartLabels = filteredData.map(
    (d: ProcessedData) => d.noticeTypeLabel || d.filingType || ''
  );
  const chartDataValues = filteredData.map((d: ProcessedData) =>
    selectedMetric === Metric.Revenue ? d.revenue / 100 : d.volume
  );

  const pieChartData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          data: chartDataValues,
          backgroundColor: FILINGTYPE_COLORS.map(
            (color, i) =>
              color || FILINGTYPE_COLORS[i % FILINGTYPE_COLORS.length]
          ),
          borderWidth: 1
        }
      ]
    }),
    [chartLabels, chartDataValues]
  );

  const barChartData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: chartLabels.map((label, i) => ({
        label,
        data: chartLabels.map((_, j) => (i === j ? chartDataValues[j] : 0)),
        backgroundColor: FILINGTYPE_COLORS[i % FILINGTYPE_COLORS.length],
        borderWidth: 1,
        barThickness: 20
      }))
    }),
    [chartLabels, chartDataValues, selectedMetric]
  );

  const totalMetric = useMemo(
    () =>
      filteredData.reduce(
        (sum: number, item: ProcessedData) =>
          sum + item[selectedMetric as Metric],
        0
      ),
    [filteredData, selectedMetric]
  );

  const chartTooltipOptions = useMemo(
    () => ({
      plugins: {
        tooltip: {
          callbacks: {
            label(context: any) {
              let value: number;
              let percent = 0;
              let label = '';
              if (
                context.chart.config.type === 'pie' ||
                context.chart.config.type === 'doughnut'
              ) {
                value = context.parsed;
                const total = (context.dataset.data as number[]).reduce(
                  (a: number, b: number) => a + b,
                  0
                );
                percent = total ? (value / total) * 100 : 0;
                if (selectedMetric === Metric.Revenue) {
                  label = `${context.label}: $${Number(value).toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }
                  )} (${percent.toFixed(1)}%)`;
                } else {
                  label = `${context.label}: ${value} (${percent.toFixed(1)}%)`;
                }
              } else {
                value = context.parsed.x ?? 0;
                const { datasets } = context.chart.data;
                const total = datasets.reduce((sum: number, ds: any) => {
                  return (
                    sum +
                    ds.data.reduce(
                      (dsum: number, v: number) =>
                        dsum + (typeof v === 'number' ? v : 0),
                      0
                    )
                  );
                }, 0);
                percent = total ? (value / total) * 100 : 0;
                if (selectedMetric === Metric.Revenue) {
                  label = `$${Number(value).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} (${percent.toFixed(1)}%)`;
                } else {
                  label = `${value} (${percent.toFixed(1)}%)`;
                }
              }
              return label;
            }
          }
        }
      }
    }),
    [selectedMetric]
  );

  const handleExportToCSV = async () => {
    if (selectedOrderType === Product.Notice) {
      const notices = await fetchNoticesWithParams(
        getOrgIds,
        getDateRange,
        withLoading
      );

      exportSearchableRecordsToCSV({
        dataAndColumns: [
          {
            data: notices,
            columns: REPORT_V2_NOTICE_CSV_COLUMNS
          }
        ],
        filename: `product_category_report_notices_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      });
    } else {
      const newspaperOrders = await fetchOrdersWithParams(
        getOrgIds,
        getDateRange,
        withLoading
      );

      const filteredOrders = newspaperOrders.filter(
        o => o.ordertype === selectedOrderType
      );

      exportSearchableRecordsToCSV({
        dataAndColumns: [
          {
            data: filteredOrders,
            columns: NEWSPAPER_ORDER_CSV_COLUMNS
          }
        ],
        filename: `product_category_report_${selectedOrderType}_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div>
        Error: {error.message || 'An error occurred while loading the data'}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20
        }}
      >
        <div>
          <div className="flex gap-2">
            {ORDER_TYPE_TABS.map(tab => (
              <ColumnButton
                key={tab.value}
                buttonText={tab.label}
                onClick={() => setSelectedOrderType(tab.value)}
                type="button"
                secondary={selectedOrderType === tab.value}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex gap-2 ml-4">
            {METRIC_TABS.map(tab => (
              <ColumnButton
                buttonText={capitalize(tab.label)}
                key={tab.value}
                onClick={() => setSelectedMetric(tab.value)}
                type="button"
                secondary={selectedMetric === tab.value}
              />
            ))}
          </div>
          <div style={{ marginLeft: 10 }}></div>
          <ColumnButton
            buttonText="Export CSV"
            onClick={handleExportToCSV}
            type="button"
            primary
          />
        </div>
      </div>
      {!filteredData.length ? (
        <div>No data available</div>
      ) : (
        <>
          <div className="flex justify-between items-start">
            <div className="flex flex-col w-[400px] min-w-[300px] h-[200px]">
              <h2 className="font-semibold mb-2">
                {selectedMetric === Metric.Revenue
                  ? capitalize(Metric.Revenue)
                  : capitalize(Metric.Volume)}{' '}
                by{' '}
                {selectedOrderType === Product.Notice
                  ? 'Notice Type'
                  : 'Filing Type'}
              </h2>
              {filteredData.length ? (
                <Pie
                  data={pieChartData}
                  options={{
                    ...chartTooltipOptions,
                    radius: '80%',
                    plugins: {
                      ...chartTooltipOptions.plugins,
                      legend: { display: false }
                    }
                  }}
                  width={400}
                  height={250}
                />
              ) : (
                <div className="min-h-[250px] flex items-center justify-center">
                  No data available
                </div>
              )}
            </div>
            <div className="flex flex-col w-[600px] min-w-[600px] h-[300px]">
              <div className="flex justify-between items-center mb-2">
                <h2 className="font-semibold">
                  {selectedMetric === Metric.Revenue
                    ? capitalize(Metric.Revenue)
                    : capitalize(Metric.Volume)}{' '}
                  by{' '}
                  {selectedOrderType === Product.Notice
                    ? 'Notice Type'
                    : 'Filing Type'}
                </h2>
                <div className="text-m">
                  Total{' '}
                  {selectedMetric === Metric.Revenue
                    ? Metric.Revenue
                    : Metric.Volume}
                  :{' '}
                  {selectedMetric === Metric.Revenue
                    ? formatPriceWithCents('$', totalMetric)
                    : totalMetric}
                </div>
              </div>

              {filteredData.length ? (
                <Bar
                  data={barChartData}
                  options={{
                    ...chartTooltipOptions,
                    indexAxis: 'y',
                    plugins: {
                      ...chartTooltipOptions.plugins,
                      legend: { display: false }
                    }
                  }}
                  width={600}
                  height={300}
                />
              ) : (
                <div className="min-h-[300px] flex items-center justify-center">
                  No data available
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
